import { useEffect, useRef, useState } from 'react';
import ComponentWrapper from '../../fragments/component-wrapper/ComponentWrapper';

// MySchoolApp inquiry embeds only start posting resize updates when the
// `keyid=local` route param is present (see their AutoSizeIframe/OnBoard.InquiryForm.Resize logic).
const MYSCHOOLAPP_INQUIRY_PATTERN = /myschoolapp\.com\/app\/embed#inquiry\//i;

function withAutoResizeParams(url) {
  if (!MYSCHOOLAPP_INQUIRY_PATTERN.test(url) || /keyid=/i.test(url)) {
    return url;
  }
  return `${url}/keyid=local/selector=iframe-embed`;
}

function parseHeightValue(value) {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export default function IframeEmbed({ block }) {
  const rawEmbedUrl = block?.embedUrl || '';
  const title = block?.title || 'Embedded form';
  const parsedHeight = Number.parseInt(block?.height, 10);
  const fallbackHeight =
    Number.isFinite(parsedHeight) && parsedHeight > 0 ? parsedHeight : 500;
  const allowScrolling = block?.allowScrolling !== false;

  const iframeRef = useRef(null);
  const [height, setHeight] = useState(fallbackHeight);
  const embedUrl = withAutoResizeParams(rawEmbedUrl);

  useEffect(() => {
    function handleMessage(event) {
      if (event.source !== iframeRef.current?.contentWindow) return;

      const data = event.data;
      const measured =
        parseHeightValue(data?.newHeight) ?? parseHeightValue(data?.height);
      if (measured) setHeight(measured);
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    function measureSameOrigin() {
      try {
        const measured =
          iframe.contentWindow?.document?.documentElement?.scrollHeight;
        if (measured) setHeight(measured);
      } catch {
        // cross-origin iframe: height updates must arrive via postMessage instead
      }
    }

    iframe.addEventListener('load', measureSameOrigin);
    return () => iframe.removeEventListener('load', measureSameOrigin);
  }, []);

  if (!rawEmbedUrl) return null;

  return (
    <ComponentWrapper className="IframeEmbed" padding={false}>
      <div className="l-grid">
        <div
          className={`l-grid__col l-grid__col--l-6-12 l-grid__col--l-push-3-12`}
        >
          {block.heading && (
            <h1 className="IframeEmbed-heading h5">{block.heading}</h1>
          )}
          <div className="IframeEmbed-inner">
            <iframe
              ref={iframeRef}
              src={embedUrl}
              title={title}
              className="IframeEmbed-iframe"
              style={{ height: `${height}px` }}
              // scrolling={allowScrolling ? 'yes' : 'no'}
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
            />
          </div>
        </div>
      </div>
    </ComponentWrapper>
  );
}
